import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Card,
  Form,
  Select,
  Input,
  Button,
  Modal,
  message,
} from '@surecompanies/core_components'
import dayjs from 'dayjs'
import {
  getPaymentAssignmentStatusLabel,
  PaymentAssignmentStatusId,
  PaymentStatus,
  PRIORITY_LABELS,
  PriorityList,
} from '../Utils/types'
import { PaymentAssignmentRole } from '../Utils/roles'
import {
  SectionCardWrapper,
  SectionTitle,
  cardStyle,
  FieldsGrid,
  FieldGroup,
} from '../PaymentDetailModal/PaymentDetailModal.styles'
import type { PaymentCase } from '../PaymentDetailModal/PaymentDetailModal.types'
import type { PaymentAssignmentRolePolicy } from '../Utils/rolePolicy'
import { useQueryClient } from '@tanstack/react-query'
import { useSetPaymentAssignmentStatusMutation } from '../../../api/finance-api-client'
import { usePaymentAssignmentUserOptions } from '../hooks/usePaymentAssignmentUserOptions'
import { StatusFields } from './statusFields/StatusFields'
import {
  StatusUpdateFormKey,
  StatusUpdateFormKeyLabels,
  buildStatusMetadata,
  normalizeNullableId,
} from './statusUpdate.type'
import type { StatusUpdateDto } from '../../../api/dto/set-payment-assignment-status.dto'

/** Option children from JSX like `{name} ({role})` are an array of text nodes, not a string — flatten for search. */
function selectOptionSearchText(option: {
  children?: React.ReactNode
  label?: React.ReactNode
}): string {
  return React.Children.toArray(option?.label ?? option?.children)
    .map((child) =>
      typeof child === 'string' || typeof child === 'number'
        ? String(child)
        : ''
    )
    .join('')
}

export interface CaseManagementSectionProps {
  paymentCase: PaymentCase | null
  onComplete: () => void
  onStatusUpdated?: (caseId: number, status: PaymentStatus) => void
  onClose?: () => void
  canUpdateCase?: boolean
  currentPaymentAssignmentRole: PaymentAssignmentRole
  rolePolicy: PaymentAssignmentRolePolicy
}

export function CaseManagementSection({
  paymentCase,
  onComplete: _onComplete,
  onStatusUpdated,
  onClose: _onClose,
  canUpdateCase = true,
  currentPaymentAssignmentRole,
  rolePolicy,
}: CaseManagementSectionProps) {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const initialValuesRef = useRef<Record<string, unknown>>({})
  const [isFormDirty, setIsFormDirty] = useState(false)
  const setStatusMutation = useSetPaymentAssignmentStatusMutation()
  const isSubmitting = setStatusMutation.isPending
  const { options: userOptions, isLoading: makerUsersLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.MAKER, {
      enabled: rolePolicy.canAssignMaker,
    })

  const { options: escalatorOptions, isLoading: escalatorUsersLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.ESCALATOR, {
      enabled: rolePolicy.canAssignEscalator,
    })

  const canUpdateThisCase = rolePolicy.canUpdateStatus && canUpdateCase

  useEffect(() => {
    if (paymentCase) {
      const today = dayjs()
      const benefitEndDateRaw = paymentCase.benefit?.benefitEndDate
      const benefitEndDate = benefitEndDateRaw ? dayjs(benefitEndDateRaw) : null

      const latestPaymentLog = [...(paymentCase.activityLogs ?? [])]
        .filter(
          (log) =>
            log.actionType === 'Status Updated' &&
            log.statusAfter === PaymentAssignmentStatusId.PAYMENT_SUBMITTED
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )[0]

      const logMeta = latestPaymentLog?.metadata

      const initialValues: Record<string, unknown> = {
        [StatusUpdateFormKey.ASSIGNEE_TO_ID]:
          paymentCase.assigneeId ?? undefined,
        [StatusUpdateFormKey.ESCALATOR_TO_ID]:
          paymentCase.escalatorId ?? undefined,
        [StatusUpdateFormKey.STATUS]:
          paymentCase.paymentStatus?.id ?? undefined,
        [StatusUpdateFormKey.PRIORITY]: paymentCase.priority ?? undefined,
        [StatusUpdateFormKey.NOTE]: '',
        // Status-specific fields — sourced from the latest activity log entry
        [StatusUpdateFormKey.ACTUAL_PREMIUM_PAID]:
          logMeta?.[StatusUpdateFormKey.ACTUAL_PREMIUM_PAID] != null
            ? Number(logMeta[StatusUpdateFormKey.ACTUAL_PREMIUM_PAID])
            : undefined,
        [StatusUpdateFormKey.ACTUAL_PAYMENT_DATE]: logMeta?.[
          StatusUpdateFormKey.ACTUAL_PAYMENT_DATE
        ]
          ? dayjs(logMeta[StatusUpdateFormKey.ACTUAL_PAYMENT_DATE] as string)
          : today,
        [StatusUpdateFormKey.CONFIRMATION_CODE]:
          (logMeta?.[StatusUpdateFormKey.CONFIRMATION_CODE] as
            | string
            | undefined) ?? undefined,
        [StatusUpdateFormKey.CHECK_NUMBER]:
          (logMeta?.[StatusUpdateFormKey.CHECK_NUMBER] as string | undefined) ??
          undefined,
        [StatusUpdateFormKey.DID_SET_UP_AUTOPAY]:
          logMeta?.[StatusUpdateFormKey.DID_SET_UP_AUTOPAY] ?? undefined,
        [StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD]:
          (logMeta?.[StatusUpdateFormKey.AUTOPAY_PAYMENT_METHOD] as
            | string
            | undefined) ?? undefined,
        [StatusUpdateFormKey.AUTOPAY_PULL_DAY]:
          (logMeta?.[StatusUpdateFormKey.AUTOPAY_PULL_DAY] as
            | number
            | undefined) ?? undefined,
        [StatusUpdateFormKey.ESCALATE_REASON]: undefined,
        [StatusUpdateFormKey.ESCALATE_EXPLANATION]: undefined,
        [StatusUpdateFormKey.UPDATE_BENEFIT_END_DATE]:
          benefitEndDate && benefitEndDate.isValid() ? benefitEndDate : today,
        [StatusUpdateFormKey.UPDATE_EFFECTIVE_DATE]: today,
      }

      initialValuesRef.current = initialValues
      form.setFieldsValue(initialValues)
    }
  }, [paymentCase, form])

  const checkIfDirty = useCallback(
    (_: unknown, allValues: Record<string, unknown>) => {
      const initial = initialValuesRef.current
      const dirty = Object.keys(initial).some((key) => {
        const a = allValues[key]
        const b = initial[key]
        if (dayjs.isDayjs(a) && dayjs.isDayjs(b)) return !a.isSame(b)
        return a !== b
      })
      setIsFormDirty(dirty)
    },
    []
  )

  const handleResetToInitialValues = useCallback(() => {
    if (!paymentCase) return
    if (isSubmitting) return
    const values = initialValuesRef.current
    form.resetFields()
    form.setFieldsValue(values)
    setIsFormDirty(false)
  }, [form, paymentCase, isSubmitting])

  const ESCALATOR_ASSIGNABLE_STATUSES: Set<PaymentStatus> = new Set([
    PaymentAssignmentStatusId.PAID_ESCALATION,
    PaymentAssignmentStatusId.UNPAID_ESCALATION,
    PaymentAssignmentStatusId.EE_PAID,
    PaymentAssignmentStatusId.TERMINATED_PLAN,
    PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
    PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
  ])

  const currentStatusId = paymentCase?.paymentStatus?.id

  const getAvailableStatuses = (): {
    statuses: PaymentStatus[]
    disabledStatuses: Set<PaymentStatus>
  } => {
    const statuses = rolePolicy.allowedStatuses as PaymentStatus[]
    const filtered = statuses.filter(
      (s) =>
        !s.startsWith('BOT_') || s === PaymentAssignmentStatusId.BOT_CLEARED
    )

    const disabledStatuses = new Set<PaymentStatus>()
    if (currentStatusId && !filtered.includes(currentStatusId)) {
      disabledStatuses.add(currentStatusId)
      return {
        statuses: [currentStatusId, ...filtered],
        disabledStatuses,
      }
    }
    return { statuses: filtered, disabledStatuses }
  }

  const handleFormSubmit = () => {
    if (!paymentCase) return
    if (!canUpdateThisCase) {
      Modal.error({
        title: 'Read-only case',
        content: 'You can only update benefits currently assigned to you.',
        centered: true,
      })
      return
    }

    form
      .validateFields()
      .then((values) => {
        if (isSubmitting) return

        const status = values[StatusUpdateFormKey.STATUS] as PaymentStatus

        const metadata = buildStatusMetadata(status, values, {
          priority: values[StatusUpdateFormKey.PRIORITY],
          primaryPaymentMethod:
            paymentCase?.carrierApplicationPrimaryPaymentMethod,
        })

        const assigneeId = normalizeNullableId(
          values[StatusUpdateFormKey.ASSIGNEE_TO_ID]
        )
        const escalatorId = normalizeNullableId(
          values[StatusUpdateFormKey.ESCALATOR_TO_ID]
        )
        const note =
          values[StatusUpdateFormKey.NOTE] == null
            ? undefined
            : String(values[StatusUpdateFormKey.NOTE])

        const dto: StatusUpdateDto = {
          paymentAssignmentId: paymentCase.id,
          status: status,
          assigneeId,
          escalatorId,
          note: note?.trim() ? note : undefined,
          metadata,
        }

        return setStatusMutation
          .mutateAsync(dto)
          .then(() => {
            onStatusUpdated?.(paymentCase.id, status)
            setIsFormDirty(false)
            message.success('Status updated successfully')
          })
          .catch((e: any) => {
            const messageText =
              e?.response?.data?.message || e?.message || 'Status update failed'
            Modal.error({
              title: 'Status update failed',
              content: messageText,
              centered: true,
            })
            throw e
          })
          .finally(() => {
            queryClient.invalidateQueries({
              queryKey: ['payment-assignment-detail', paymentCase.id],
            })
            queryClient.invalidateQueries({
              queryKey: ['payment-assignment-benefit-events', paymentCase.id],
            })
            if (_onComplete) {
              _onComplete()
            }
          })
      })
      .catch((errorInfo) => {
        console.log('Validation failed:', errorInfo)
      })
  }

  return (
    <SectionCardWrapper>
      <Card style={cardStyle}>
        <SectionTitle>Case Management</SectionTitle>
        {!canUpdateThisCase && (
          <div style={{ marginBottom: 12, color: '#677786' }}>
            Read-only: this benefit is not assigned to you.
          </div>
        )}
        <Form form={form} layout="vertical" onValuesChange={checkIfDirty}>
          <FieldsGrid>
            <FieldGroup>
              <Form.Item
                name={StatusUpdateFormKey.STATUS}
                label={StatusUpdateFormKeyLabels[StatusUpdateFormKey.STATUS]}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                {(() => {
                  const { statuses, disabledStatuses } = getAvailableStatuses()
                  return (
                    <Select
                      placeholder="Select Status"
                      style={{ width: '100%' }}
                      labelInValue={false}
                      disabled={!canUpdateThisCase}
                    >
                      {statuses.map((status) => (
                        <Select.Option
                          key={status}
                          value={status}
                          disabled={disabledStatuses.has(status)}
                        >
                          {getPaymentAssignmentStatusLabel(status)}
                          {disabledStatuses.has(status) ? ' (current)' : ''}
                        </Select.Option>
                      ))}
                    </Select>
                  )
                })()}
              </Form.Item>
            </FieldGroup>

            {rolePolicy.canAssignMaker && (
              <FieldGroup>
                <Form.Item
                  name={StatusUpdateFormKey.ASSIGNEE_TO_ID}
                  label={
                    StatusUpdateFormKeyLabels[
                      StatusUpdateFormKey.ASSIGNEE_TO_ID
                    ]
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Unassigned"
                    loading={makerUsersLoading}
                    disabled={!canUpdateThisCase}
                    showSearch
                    filterOption={(input, option) =>
                      selectOptionSearchText(
                        option as {
                          children?: React.ReactNode
                          label?: React.ReactNode
                        }
                      )
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                    style={{ width: '100%' }}
                  >
                    {userOptions.map((u) => (
                      <Select.Option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </FieldGroup>
            )}

            <Form.Item
              noStyle
              shouldUpdate={(prev: any, curr: any) =>
                prev?.[StatusUpdateFormKey.STATUS] !==
                curr?.[StatusUpdateFormKey.STATUS]
              }
            >
              {({ getFieldValue }) => {
                const selectedStatus = getFieldValue(
                  StatusUpdateFormKey.STATUS
                ) as PaymentStatus | undefined
                const isChecker =
                  currentPaymentAssignmentRole === PaymentAssignmentRole.CHECKER
                const canShowEscalatorSelect =
                  rolePolicy.canAssignEscalator &&
                  (isChecker ||
                    (!!selectedStatus &&
                      ESCALATOR_ASSIGNABLE_STATUSES.has(selectedStatus)))

                if (!canShowEscalatorSelect) return null

                return (
                  <FieldGroup>
                    <Form.Item
                      name={StatusUpdateFormKey.ESCALATOR_TO_ID}
                      label={
                        StatusUpdateFormKeyLabels[
                          StatusUpdateFormKey.ESCALATOR_TO_ID
                        ]
                      }
                      style={{ marginBottom: 0 }}
                      rules={
                        currentPaymentAssignmentRole !==
                        PaymentAssignmentRole.CHECKER
                          ? [
                              {
                                required: true,
                                message: 'Please select an escalator',
                              },
                            ]
                          : undefined
                      }
                    >
                      <Select
                        placeholder="Unassigned"
                        loading={escalatorUsersLoading}
                        disabled={!canUpdateThisCase}
                        showSearch
                        filterOption={(input, option) =>
                          selectOptionSearchText(
                            option as {
                              children?: React.ReactNode
                              label?: React.ReactNode
                            }
                          )
                            .toLowerCase()
                            .includes(input.trim().toLowerCase())
                        }
                        style={{ width: '100%' }}
                      >
                        {escalatorOptions.map((u) => (
                          <Select.Option key={u.id} value={u.id}>
                            {u.name} ({u.role})
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </FieldGroup>
                )
              }}
            </Form.Item>

            {currentPaymentAssignmentRole === PaymentAssignmentRole.CHECKER && (
              <FieldGroup>
                <Form.Item
                  name={StatusUpdateFormKey.PRIORITY}
                  label={
                    StatusUpdateFormKeyLabels[StatusUpdateFormKey.PRIORITY]
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select priority"
                    style={{ width: '100%' }}
                    labelInValue={false}
                    disabled={!canUpdateThisCase}
                  >
                    {PriorityList.map((p) => (
                      <Select.Option key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </FieldGroup>
            )}
          </FieldsGrid>

          <Form.Item
            noStyle
            shouldUpdate={(prev: any, curr: any) =>
              prev?.[StatusUpdateFormKey.STATUS] !==
              curr?.[StatusUpdateFormKey.STATUS]
            }
          >
            {({ getFieldValue }) => {
              const status = getFieldValue(StatusUpdateFormKey.STATUS)
              const readOnly = !canUpdateThisCase
              return (
                <fieldset
                  disabled={readOnly}
                  aria-disabled={readOnly}
                  style={{
                    border: 'none',
                    margin: 0,
                    padding: 0,
                    minInlineSize: 'auto',
                    // AntD Select/DatePicker aren't native controls, so a
                    // disabled <fieldset> alone won't block them. Suppress all
                    // interaction and dim the group when read-only.
                    ...(readOnly
                      ? {
                          pointerEvents: 'none' as const,
                          opacity: 0.6,
                        }
                      : {}),
                  }}
                >
                  <StatusFields
                    status={status}
                    rolePolicy={rolePolicy}
                    primaryPaymentMethod={
                      paymentCase?.carrierApplicationPrimaryPaymentMethod
                    }
                  />
                </fieldset>
              )
            }}
          </Form.Item>

          <div style={{ marginTop: '16px' }}>
            <Form.Item
              name={StatusUpdateFormKey.NOTE}
              label={StatusUpdateFormKeyLabels[StatusUpdateFormKey.NOTE]}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                rows={3}
                placeholder="Add a comment or note..."
                style={{ resize: 'vertical' }}
                disabled={!canUpdateThisCase}
              />
            </Form.Item>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '16px',
            }}
          >
            <Button
              onClick={handleResetToInitialValues}
              disabled={!canUpdateThisCase}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={handleFormSubmit}
              loading={isSubmitting}
              disabled={!canUpdateThisCase || !isFormDirty}
            >
              Update
            </Button>
          </div>
        </Form>
      </Card>
    </SectionCardWrapper>
  )
}
